import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import {
  AlertController,
  CheckboxCustomEvent,
  ModalController,
  Platform,
  ToastController
} from '@ionic/angular';
import { mergeMap, Observable, of, Subscription } from 'rxjs';
import { Device } from '@capacitor/device';
import {
  NativeSettings,
  AndroidSettings,
  IOSSettings
} from 'capacitor-native-settings';
import {
  Anlegg,
  BrukerkundeResponse,
  KeycloakUserInfo,
  KundeResponse,
  SamtykkeResponse
} from 'src/app/services/bruker/bruker.types';
import { BrukerService } from '../../../services/bruker/bruker.service';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { LoadingController } from '@ionic/angular';
import { KundetilgangModal } from '../../kundetilgang-modal/kundetilgang-modal.page';
import { forsideRoute } from '../../../routes';
import { DetaljsidePage } from '../editcontact/detaljside.page';
import { EndrepassordsidePage } from '../endrepassord/endrepassordside.page';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { SlettebrukerComponent } from '../slettebruker/slettebruker/slettebruker.component';
import { FeatureTogglesService } from 'src/app/services/feature-toggles/feature-toggles.service';
import { FeatureToggle } from 'src/app/services/feature-toggles/toggles.types';
import { PushNotificationsService } from 'src/app/services/push-notifications/push-notifications.service';
import { FcmTokenService } from 'src/app/services/fcm-token/fcm-token.service';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import {
  FcmPushPreference,
  FcmToken
} from 'src/app/services/fcm-token/fcm-token.types';
import { Router } from '@angular/router';
import { DataService } from 'src/app/services/datatransfer/DataService';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { addDays } from 'date-fns';

@Component({
  selector: 'app-aboutme',
  templateUrl: 'aboutMe.component.html',
  styleUrls: ['aboutMe.component.scss']
})
export class AboutMeComponent implements OnInit {
  @Input() userInfo: KeycloakUserInfo;
  @Input() kundeId: string;

  _kundeResponse: Observable<KundeResponse>;
  _samtykkeResponse: Observable<SamtykkeResponse>;
  showError: boolean;
  fraLagretVerdi: number;
  _brukerkundeResponse: BrukerkundeResponse;
  _showBrukerkunde = false;
  isUserEier = false;

  toggleResponse: Observable<FeatureToggle[]>;
  isEndreKontaktinfoToggled: boolean;
  canChangeKunde = false;
  isChangeAddressToggled: boolean;
  showEnodeInMenu: boolean;

  modalPage: any;
  anlegg: Anlegg[];

  permission = false;
  isPushAvailable: boolean;
  pushPreference: FcmPushPreference;
  fcmToken: FcmToken;
  checkedTomorrowSpot: boolean;
  checkedDayMaxExceeded: boolean;

  kundeSubscription: Subscription;
  samtykkeSubscription: Subscription;
  toggleSubscription: Subscription;
  dataSubscription: Subscription;
  brukerSubscription: Subscription;

  constructor(
    private _toast: ToastController,
    public loadingController: LoadingController,
    public modalCtrl: ModalController,
    private iab: InAppBrowser,
    private analyticsService: AnalyticsService,
    public keycloak: KeycloakService,
    public platform: Platform,
    private _brukerService: BrukerService,
    private toggleService: FeatureTogglesService,
    private fcmTokenService: FcmTokenService,
    private pushService: PushNotificationsService,
    private alertController: AlertController,
    private cdRef: ChangeDetectorRef,
    private dataService: DataService,
    private router: Router,
    private settingsService: SettingsService
  ) {
    this.toggleResponse = this.toggleService.getActiveFeatureToggles();
    this.isPushAvailable = Capacitor.isPluginAvailable('PushNotifications');

    this.dataSubscription = this.dataService.getKundebytte().subscribe((x) => {
      if (x) {
        this.keycloak
          .getKeycloakInstance()
          .loadUserInfo()
          .then((userInfo: KeycloakUserInfo) => {
            this.userInfo = userInfo;
            this.kundeId = userInfo.kunde_id;
            this.ngOnInit();
          });
      }
    });

    // Hvis appen lukkes og åpnes igjen, sjekk om vi har nye permissions
    this.platform.resume.subscribe(async () => {
      const isProfilActive = this.router.url.includes('profil');
      if (this.isPushAvailable && isProfilActive) {
        PushNotifications.checkPermissions().then(async (res) => {
          if (res.receive === 'granted') {
            if (!this.permission || !this.pushPreference) {
              await PushNotifications.register();
            }
            await this.getPushPreferences();

            // Hvis vi ga samtykke nettopp, subscribe til ønsket topic
            if (!this.permission && this.pushPreference?.tomorrowSpot) {
              this.pushService.subscribeToMyOmraader();
            }
            this.permission = true;
          } else {
            this.permission = false;
          }
          this.cdRef.detectChanges();
        });
      }
    });
  }

  ionViewWillLeave() {
    this.kundeSubscription.unsubscribe();
    this.samtykkeSubscription.unsubscribe();
    this.toggleSubscription.unsubscribe();
    this.brukerSubscription.unsubscribe();
    this.dataSubscription.unsubscribe();
  }

  async ngOnInit() {
    this.isUserEier = this.keycloak.isUserInRole('eier');
    this.settingsService.showEnodeInMenu$.subscribe((res) => {
      this.showEnodeInMenu = res;
    });
    this.showError = false;
    this._kundeResponse = this._brukerService.getKunde();
    this._samtykkeResponse = this._brukerService.getSamtykke();
    this.brukerSubscription = this._brukerService
      .getBrukerkunde()
      .subscribe((res) => {
        this._brukerkundeResponse = res;
        this.canChangeKunde = !res?.inUse?.tildelt || this.isUserEier;
        if (res.kundenumre && res.kundenumre.length > 1) {
          this._showBrukerkunde = true;
        }
      });
    this.kundeSubscription = this._kundeResponse.subscribe((x) => {
      this.anlegg = x.kunde.anleggsListe;
    });
    this.toggleSubscription = this.toggleResponse.subscribe((res) => {
      this.isEndreKontaktinfoToggled =
        this.getIsEndreKontaktinfoToggled(res) ?? false;
      this.isChangeAddressToggled =
        this.getIsChangeAddressToggled(res) ?? false;
    });
    if (this.isPushAvailable) {
      this.getPushPreferences();
      PushNotifications.checkPermissions().then((res) => {
        if (res.receive === 'granted') {
          this.permission = true;
        } else {
          this.permission = false;
        }
      });
    }
  }

  async getPushPreferences() {
    const deviceId = await Device.getId();
    const info = await Device.getInfo();
    const deviceName = deviceId.identifier + ':' + info.name;

    this.fcmTokenService
      .getFcmTokenWithPreferences(deviceName, this.kundeId)
      .pipe(
        mergeMap((res) => {
          this.pushPreference = res.preferences;
          this.fcmToken = res.token;
          this.checkedTomorrowSpot = res.preferences?.tomorrowSpot;
          this.checkedDayMaxExceeded = res.preferences?.dayMaxExceeded;
          this.cdRef.detectChanges();

          // Renew token date every 30 days, since stale tokens are deleted after 60 days
          const tokenDate = new Date(res?.token?.insertDateTime);
          const timeToRenewToken =
            tokenDate && tokenDate < addDays(new Date(), -30);
          if (timeToRenewToken) {
            const payload = JSON.stringify({
              RegToken: res.token.regToken,
              KundeId: res.token.kundeId,
              DeviceName: res.token.deviceName
            });
            return this.fcmTokenService.postFcmToken(payload);
          } else {
            return of(null);
          }
        })
      )
      .subscribe();
  }

  requestPermissions() {
    PushNotifications.requestPermissions().then(async (res) => {
      if (res.receive === 'denied') {
        const alert = await this.alertController.create({
          cssClass: 'push-alert',
          mode: 'ios',
          header: 'Pushvarslinger',
          message:
            'Du har skrudd av varslinger for Ishavskraft-appen. Gå til innstillinger på telefonen for å skru dette på igjen.',
          buttons: [
            {
              text: 'Avbryt',
              role: 'cancel'
            },
            {
              text: 'Innstillinger',
              role: 'confirm',
              handler: () => {
                NativeSettings.open({
                  optionAndroid: AndroidSettings.ApplicationDetails,
                  optionIOS: IOSSettings.App
                });
              }
            }
          ]
        });
        await alert.present();
      }
    });
  }

  async postPushPrefPayload(toggledValue: { [key: string]: boolean }) {
    const deviceId = await Device.getId();
    const info = await Device.getInfo();

    const payload = {
      deviceName: deviceId.identifier + ':' + info.name,
      kundeId: this.kundeId,
      tomorrowSpot: this.checkedTomorrowSpot,
      dayMaxExceeded: this.checkedDayMaxExceeded,
      ...toggledValue
    };

    this.fcmTokenService
      .postPushPreferences(JSON.stringify(payload))
      .subscribe();
  }

  async toggleTomorrowSpotPush($event: CheckboxCustomEvent) {
    this.checkedTomorrowSpot = $event.target.checked;
    if ($event.target.checked) {
      this.pushService.subscribeToMyOmraader();
    } else {
      // this.pushService.unsubscribeFromMyOmraader();
      // Håndtere uforutsette feil, kjører alt
      this.pushService.unsubscribeFromAllSpotTopics();
    }
    this.postPushPrefPayload({
      tomorrowSpot: $event.target.checked
    });
  }

  async toggleDayMaxExceededPush($event: CheckboxCustomEvent) {
    this.checkedDayMaxExceeded = $event.target.checked;
    this.postPushPrefPayload({
      dayMaxExceeded: $event.target.checked
    });
  }

  toggleShowEnodeInMenu() {
    this.settingsService.setShowEnodeInMenu(!this.showEnodeInMenu);
  }

  getIsEndreKontaktinfoToggled(toggles: FeatureToggle[]): boolean {
    const myToggle = toggles.find((x) => x.navn === 'uc_endre_kontaktinfo');
    return myToggle ? myToggle.toggleActive === 'NOW' : false;
  }

  getIsChangeAddressToggled(toggles: FeatureToggle[]): boolean {
    const myToggle = toggles.find(
      (x) => x.navn === 'endre_adresse_kontaktinfo'
    );
    return myToggle ? myToggle.toggleActive === 'NOW' : false;
  }

  delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  navigateTo(url: string) {
    if (this.platform.is('android')) {
      this.iab.create(url, '_self');
    } else {
      //IOS
      const browser = this.iab.create(url, '_blank', 'location=no');
      browser.show();
    }
  }

  async navigateToContactEdit() {
    this.modalPage = await this.modalCtrl.create({
      component: DetaljsidePage,
      cssClass: 'endre-kontaktinfo-modal-content',
      backdropDismiss: true,
      componentProps: {
        canChangeAddress: this.isChangeAddressToggled
      }
    });
    this.analyticsService.logEvent_selectContent(
      'profil åpne endre-kontaktinfo'
    );
    await this.modalPage.present();

    this.modalPage.onDidDismiss().then((detail) => {
      if (detail.data?.oppdatertKunde) {
        // Fetch new data
        this._kundeResponse = this._brukerService.getKunde();
      }
    });
  }

  async navigateToPasswordEdit() {
    this.modalPage = await this.modalCtrl.create({
      component: EndrepassordsidePage,
      cssClass: 'endre-passord-modal-content',
      backdropDismiss: true
    });
    this.analyticsService.logEvent_selectContent('profil åpne endre-passord');
    await this.modalPage.present();
  }

  navigateToForside() {
    const loggedIn = this.keycloak.isLoggedIn();
    if (loggedIn) {
      this.router.navigate([forsideRoute]);
    }
  }

  async changeSamtykke(whichSamtykke: number, $event) {
    //var platformmode: any = this.platform.is('android') ? "ios" : "md";
    const loading = await this.loadingController.create({
      message: 'Vennligst vent...',
      mode: this.platform.is('android') ? 'md' : 'ios'
    });
    await loading.present();

    this.brukerSubscription = this._brukerService
      .sendSamtykke(whichSamtykke, $event.target.checked)
      .subscribe({
        next: (response) => {
          this._toast
            .create({
              message: response.message,
              duration: 2000,
              position: 'middle',
              color: 'success'
            })
            .then((toast) => {
              loading.dismiss();
              toast.present();
              this.analyticsService.logEvent_selectContent(
                'samtykke ' +
                  whichSamtykke +
                  ' endret til ' +
                  $event.target.checked
              );
              if (whichSamtykke === 4) {
                this.analyticsService.setCollectionEnabled(
                  $event.target.checked
                );
              }
            })
            .catch((response) => {
              this._toast
                .create({
                  message: response.message,
                  duration: 4000,
                  position: 'middle',
                  color: 'danger'
                })
                .then((toast) => {
                  loading.dismiss();
                  toast.present();
                });
            });
          //this.freezeUI(false);
        },
        error: (err) => {
          this._toast
            .create({
              message:
                'Det skjedde dessverre en feil ved oppdatering av samtykke. Prøv igjen senere eller kontakt oss om problemet vedvarer.',
              duration: 8000,
              position: 'middle',
              color: 'danger'
            })
            .then((toast) => {
              loading.dismiss();
              toast.present();
            });
        }
      });
  }

  public async showBrukerkunde() {
    this.modalPage = await this.modalCtrl.create({
      component: KundetilgangModal,
      cssClass: 'endre-kundetilganger-modal-content',
      backdropDismiss: true
    });
    await this.modalPage.present();

    this.modalPage.onDidDismiss().then((detail) => {
      if (detail.data?.changedActiveBruker) {
        // Reload component
        this.ngOnInit();
      }
    });
  }

  async deleteKCUser() {
    if (!this.userInfo) {
      this.userInfo = await this.keycloak.getKeycloakInstance().loadUserInfo();
    }
    //const userInfo = await this.keycloak.getKeycloakInstance().loadUserInfo();
    if (this.userInfo) {
      this.modalPage = await this.modalCtrl.create({
        component: SlettebrukerComponent,
        cssClass: 'slette-bruker-modal-content',
        backdropDismiss: true,
        componentProps: { userInfo: this.userInfo }
      });
      this.analyticsService.logEvent_selectContent('profil åpne slett-bruker');
      await this.modalPage.present();
    } else {
      this._toast
        .create({
          message:
            'Kunne ikke hente brukerinfo. Vennligst prøv igjen eller kontakt oss om problemet vedvarer.',
          duration: 8000,
          position: 'middle',
          color: 'danger'
        })
        .then((toast) => {
          toast.present();
        });
    }
  }
}
